import type { EditorialSource } from './editorial';
export interface Airport {
  id: string; name: string; iataCode: string; icaoCode?: string; city: string; state: string;
  region: 'North' | 'South' | 'East' | 'West'; type: 'International' | 'Domestic';
  /** Approximate locations for travel estimates, never operational navigation. */
  coordinates: { lat: number; lng: number };
  imageUrl: string; description: string; planningNotes: string[];
  sources: EditorialSource[]; reviewedAt: string; lastUpdated: string; adsEligible: boolean;
}

// Operational schedules, prices and ratings are intentionally not inferred from directory entries.
export const airports: Airport[] = [
  {
    "id": "lagos-murtala-muhammed",
    "name": "Murtala Muhammed International Airport",
    "iataCode": "LOS",
    "icaoCode": "DNMM",
    "city": "Lagos",
    "state": "Lagos State",
    "region": "South",
    "type": "International",
    "coordinates": {
      "lat": 6.5774,
      "lng": 3.3212
    },
    "imageUrl": "https://images.unsplash.com/photo-1570710891163-6d3b5c47248b?q=80&w=2074&auto=format&fit=crop",
    "description": "Lagos Murtala Muhammed Airport serves Ikeja and the wider Lagos area. Domestic and international departures use different terminal buildings, so the airport code alone is not enough to choose your drop-off point.",
    "planningNotes": [
      "Confirm MMIA Terminal 1 or 2 for an international flight, or MMA2 versus the FAAN domestic terminal for a domestic flight. Share the terminal name with your driver. A connection between separate buildings needs a transport plan as well as time for baggage collection and check-in."
    ],
    "sources": [
      {
        "title": "FAAN international airport directory",
        "url": "https://faan.gov.ng/international-airports/"
      },
      {
        "title": "FAAN passenger assistance and terminal directions",
        "url": "https://faan.gov.ng/passenger-experience/"
      },
      {
        "title": "MMA2 terminal operator",
        "url": "https://mma2.ng/"
      }
    ],
    "reviewedAt": "2026-10-23",
    "lastUpdated": "2026-10-23",
    "adsEligible": false
  },
  {
    "id": "abuja-nnamdi-azikiwe",
    "name": "Nnamdi Azikiwe International Airport",
    "iataCode": "ABV",
    "icaoCode": "DNAA",
    "city": "Abuja",
    "state": "Federal Capital Territory",
    "region": "North",
    "type": "International",
    "coordinates": {
      "lat": 9.0068,
      "lng": 7.2631
    },
    "imageUrl": "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=800&q=80",
    "description": "Nnamdi Azikiwe International Airport serves Abuja in the Federal Capital Territory. FAAN publishes separate assistance directions for its domestic and international terminals.",
    "planningNotes": [
      "Match your driver’s destination to the domestic or international terminal on your booking. For an onward connection, ask the airline whether baggage is checked through and whether you must return to landside check-in."
    ],
    "sources": [
      {
        "title": "FAAN international airport directory",
        "url": "https://faan.gov.ng/international-airports/"
      },
      {
        "title": "FAAN passenger assistance and terminal directions",
        "url": "https://faan.gov.ng/passenger-experience/"
      }
    ],
    "reviewedAt": "2026-10-23",
    "lastUpdated": "2026-10-23",
    "adsEligible": false
  },
  {
    "id": "port-harcourt-international",
    "name": "Port Harcourt International Airport",
    "iataCode": "PHC",
    "icaoCode": "DNPO",
    "city": "Port Harcourt",
    "state": "Rivers State",
    "region": "South",
    "type": "International",
    "coordinates": {
      "lat": 5.0155,
      "lng": 6.9496
    },
    "imageUrl": "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=800&q=80",
    "description": "Port Harcourt International Airport is at Omagwa in Rivers State. Use PHC when comparing flights to the Port Harcourt area.",
    "planningNotes": [
      "Give your pickup contact the airport name and flight number, rather than only saying Port Harcourt. If your journey continues to an industrial site or another city, arrange that transfer with your host before arrival."
    ],
    "sources": [
      {
        "title": "FAAN international airport directory",
        "url": "https://faan.gov.ng/international-airports/"
      },
      {
        "title": "FAAN airport directory and passenger guidance",
        "url": "https://faan.gov.ng/local-airports/"
      }
    ],
    "reviewedAt": "2026-10-23",
    "lastUpdated": "2026-10-23",
    "adsEligible": false
  },
  {
    "id": "kano-international",
    "name": "Mallam Aminu Kano International Airport",
    "iataCode": "KAN",
    "icaoCode": "DNKN",
    "city": "Kano",
    "state": "Kano State",
    "region": "North",
    "type": "International",
    "coordinates": {
      "lat": 12.0476,
      "lng": 8.5246
    },
    "imageUrl": "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=800&q=80",
    "description": "Mallam Aminu Kano International Airport serves Kano. The airport has domestic and international facilities; a ticket or airline confirmation determines the terminal for your journey.",
    "planningNotes": [
      "For a pilgrimage or group journey, obtain the reporting point from the organizing carrier. For other trips, check the flight’s terminal directly rather than assuming the same check-in location applies to every service."
    ],
    "sources": [
      {
        "title": "FAAN international airport directory",
        "url": "https://faan.gov.ng/international-airports/"
      },
      {
        "title": "FAAN passenger assistance and terminal directions",
        "url": "https://faan.gov.ng/passenger-experience/"
      }
    ],
    "reviewedAt": "2026-10-23",
    "lastUpdated": "2026-10-23",
    "adsEligible": false
  },
  {
    "id": "enugu-international",
    "name": "Akanu Ibiam International Airport",
    "iataCode": "ENU",
    "icaoCode": "DNEN",
    "city": "Enugu",
    "state": "Enugu State",
    "region": "East",
    "type": "International",
    "coordinates": {
      "lat": 6.4742,
      "lng": 7.562
    },
    "imageUrl": "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=800&q=80",
    "description": "Akanu Ibiam International Airport serves Enugu and travelers heading to other parts of southeastern Nigeria.",
    "planningNotes": [
      "If Enugu is your gateway to another city, compare the entire journey including the road transfer. Keep your host’s pickup details available offline and reconfirm the operating flight before leaving for the airport."
    ],
    "sources": [
      {
        "title": "FAAN international airport directory",
        "url": "https://faan.gov.ng/international-airports/"
      },
      {
        "title": "FAAN Act 2022: airport names and codes (First Schedule)",
        "url": "https://faan.gov.ng/wp-content/uploads/2025/11/FAAN-Act-2022-1.pdf"
      }
    ],
    "reviewedAt": "2026-10-23",
    "lastUpdated": "2026-10-23",
    "adsEligible": false
  },
  {
    "id": "calabar-international",
    "name": "Margaret Ekpo International Airport",
    "iataCode": "CBQ",
    "icaoCode": "DNCA",
    "city": "Calabar",
    "state": "Cross River State",
    "region": "South",
    "type": "International",
    "coordinates": {
      "lat": 4.976,
      "lng": 8.3472
    },
    "imageUrl": "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=800&q=80",
    "description": "Margaret Ekpo International Airport serves Calabar in Cross River State. Its booking code is CBQ.",
    "planningNotes": [
      "Calabar and Uyo have different airport codes. Check that the code on your ticket matches the pickup airport, particularly when comparing travel options for a visit to Cross River State."
    ],
    "sources": [
      {
        "title": "FAAN airport directory and passenger guidance",
        "url": "https://faan.gov.ng/local-airports/"
      },
      {
        "title": "FAAN Act 2022: airport names and codes (First Schedule)",
        "url": "https://faan.gov.ng/wp-content/uploads/2025/11/FAAN-Act-2022-1.pdf"
      }
    ],
    "reviewedAt": "2026-10-23",
    "lastUpdated": "2026-10-23",
    "adsEligible": false
  },
  {
    "id": "kaduna-international",
    "name": "Kaduna International Airport",
    "iataCode": "KAD",
    "icaoCode": "DNKA",
    "city": "Kaduna",
    "state": "Kaduna State",
    "region": "North",
    "type": "International",
    "coordinates": {
      "lat": 10.696,
      "lng": 7.3201
    },
    "imageUrl": "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=800&q=80",
    "description": "Kaduna Airport serves Kaduna State. FAAN identifies the airport separately from Zaria aerodrome, which is also in Kaduna State.",
    "planningNotes": [
      "Use KAD for this airport and confirm the service on your intended date. Do not substitute a Zaria address when arranging transport: the two airport locations are different."
    ],
    "sources": [
      {
        "title": "FAAN airport directory and passenger guidance",
        "url": "https://faan.gov.ng/local-airports/"
      },
      {
        "title": "FAAN Act 2022: airport names and codes (First Schedule)",
        "url": "https://faan.gov.ng/wp-content/uploads/2025/11/FAAN-Act-2022-1.pdf"
      }
    ],
    "reviewedAt": "2026-10-23",
    "lastUpdated": "2026-10-23",
    "adsEligible": false
  },
  {
    "id": "maiduguri-international",
    "name": "Maiduguri International Airport",
    "iataCode": "MIU",
    "icaoCode": "DNMA",
    "city": "Maiduguri",
    "state": "Borno State",
    "region": "North",
    "type": "International",
    "coordinates": {
      "lat": 11.8553,
      "lng": 13.0809
    },
    "imageUrl": "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=800&q=80",
    "description": "Maiduguri Airport serves Maiduguri in Borno State. FAAN lists the airport among its northern Nigerian locations.",
    "planningNotes": [
      "Coordinate onward travel with your host and consult current destination advice before booking. An operating airport does not establish the safety or availability of every onward road journey."
    ],
    "sources": [
      {
        "title": "FAAN airport directory and passenger guidance",
        "url": "https://faan.gov.ng/local-airports/"
      },
      {
        "title": "FAAN Act 2022: airport names and codes (First Schedule)",
        "url": "https://faan.gov.ng/wp-content/uploads/2025/11/FAAN-Act-2022-1.pdf"
      }
    ],
    "reviewedAt": "2026-10-23",
    "lastUpdated": "2026-10-23",
    "adsEligible": false
  },
  {
    "id": "jos-yakubu-gowon",
    "name": "Yakubu Gowon Airport",
    "iataCode": "JOS",
    "icaoCode": "DNJO",
    "city": "Jos",
    "state": "Plateau State",
    "region": "North",
    "type": "Domestic",
    "coordinates": {
      "lat": 9.6398,
      "lng": 8.8687
    },
    "imageUrl": "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=800&q=80",
    "description": "Yakubu Gowon Airport serves Jos in Plateau State. Its IATA booking code is JOS.",
    "planningNotes": [
      "Ask your airline to confirm the flight’s operating date before arranging a pickup. Share the airport location with your driver and allow for the separate journey between the airport and your accommodation."
    ],
    "sources": [
      {
        "title": "FAAN Act 2022: airport names and codes (First Schedule)",
        "url": "https://faan.gov.ng/wp-content/uploads/2025/11/FAAN-Act-2022-1.pdf"
      }
    ],
    "reviewedAt": "2026-10-23",
    "lastUpdated": "2026-10-23",
    "adsEligible": false
  },
  {
    "id": "benin-city",
    "name": "Benin Airport",
    "iataCode": "BNI",
    "icaoCode": "DNBE",
    "city": "Benin City",
    "state": "Edo State",
    "region": "South",
    "type": "Domestic",
    "coordinates": {
      "lat": 6.3168,
      "lng": 5.5995
    },
    "imageUrl": "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=800&q=80",
    "description": "Benin Airport serves Benin City in Edo State. FAAN places it in the Ogba–Oko area.",
    "planningNotes": [
      "Use BNI for Benin City, Nigeria. The city should not be confused with the neighboring country of Benin when searching for flights or discussing your itinerary with an agent."
    ],
    "sources": [
      {
        "title": "FAAN airport directory and passenger guidance",
        "url": "https://faan.gov.ng/local-airports/"
      },
      {
        "title": "FAAN Act 2022: airport names and codes (First Schedule)",
        "url": "https://faan.gov.ng/wp-content/uploads/2025/11/FAAN-Act-2022-1.pdf"
      }
    ],
    "reviewedAt": "2026-10-23",
    "lastUpdated": "2026-10-23",
    "adsEligible": false
  },
  {
    "id": "ibadan",
    "name": "Ibadan Airport",
    "iataCode": "IBA",
    "icaoCode": "DNIB",
    "city": "Ibadan",
    "state": "Oyo State",
    "region": "West",
    "type": "Domestic",
    "coordinates": {
      "lat": 7.3626,
      "lng": 3.9785
    },
    "imageUrl": "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=800&q=80",
    "description": "Ibadan Airport serves Ibadan in Oyo State and is located in the Alakia area.",
    "planningNotes": [
      "Confirm that your chosen airline is operating from Ibadan on the travel date before committing to a separate transfer. If comparing Ibadan with Lagos, include the cost and time of the ground journey in your decision."
    ],
    "sources": [
      {
        "title": "FAAN airport directory and passenger guidance",
        "url": "https://faan.gov.ng/local-airports/"
      },
      {
        "title": "FAAN Act 2022: airport names and codes (First Schedule)",
        "url": "https://faan.gov.ng/wp-content/uploads/2025/11/FAAN-Act-2022-1.pdf"
      }
    ],
    "reviewedAt": "2026-10-23",
    "lastUpdated": "2026-10-23",
    "adsEligible": false
  },
  {
    "id": "ilorin",
    "name": "Ilorin International Airport",
    "iataCode": "ILR",
    "icaoCode": "DNIL",
    "city": "Ilorin",
    "state": "Kwara State",
    "region": "North",
    "type": "International",
    "coordinates": {
      "lat": 8.4401,
      "lng": 4.494
    },
    "imageUrl": "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=800&q=80",
    "description": "Ilorin Airport serves Ilorin in Kwara State. FAAN identifies its location as the Fili area southwest of the city.",
    "planningNotes": [
      "Use ILR on your flight search and give your driver the terminal destination from the booking. A flight-time estimate from this site is not confirmation of a scheduled route."
    ],
    "sources": [
      {
        "title": "FAAN airport directory and passenger guidance",
        "url": "https://faan.gov.ng/local-airports/"
      },
      {
        "title": "FAAN Act 2022: airport names and codes (First Schedule)",
        "url": "https://faan.gov.ng/wp-content/uploads/2025/11/FAAN-Act-2022-1.pdf"
      }
    ],
    "reviewedAt": "2026-10-23",
    "lastUpdated": "2026-10-23",
    "adsEligible": false
  },
  {
    "id": "akure",
    "name": "Akure Airport",
    "iataCode": "AKR",
    "icaoCode": "DNAK",
    "city": "Akure",
    "state": "Ondo State",
    "region": "West",
    "type": "Domestic",
    "coordinates": {
      "lat": 7.2467,
      "lng": 5.301
    },
    "imageUrl": "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=800&q=80",
    "description": "Akure Airport serves the Ondo State capital. FAAN locates it at Obale, east of Akure.",
    "planningNotes": [
      "Confirm whether your host is collecting you at the airport or meeting you in the city. Save the contact number before departure so that pickup arrangements do not depend on airport Wi-Fi."
    ],
    "sources": [
      {
        "title": "FAAN airport directory and passenger guidance",
        "url": "https://faan.gov.ng/local-airports/"
      },
      {
        "title": "FAAN Act 2022: airport names and codes (First Schedule)",
        "url": "https://faan.gov.ng/wp-content/uploads/2025/11/FAAN-Act-2022-1.pdf"
      }
    ],
    "reviewedAt": "2026-10-23",
    "lastUpdated": "2026-10-23",
    "adsEligible": false
  },
  {
    "id": "asaba",
    "name": "Asaba International Airport",
    "iataCode": "ABB",
    "icaoCode": "DNAS",
    "city": "Asaba",
    "state": "Delta State",
    "region": "South",
    "type": "International",
    "coordinates": {
      "lat": 6.204,
      "lng": 6.6652
    },
    "imageUrl": "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=800&q=80",
    "description": "Asaba International Airport serves Asaba in Delta State. It is listed among the airports covered by the NCAA’s Port Harcourt regional office.",
    "planningNotes": [
      "For an onward journey beyond Asaba, agree your destination and pickup point with your driver before departure. Request mobility assistance from your airline in advance, and confirm current parking charges and drop-off arrangements directly with the airport."
    ],
    "sources": [
      {
        "title": "NCAA: Port Harcourt region airport inventory",
        "url": "https://www.ncaa.gov.ng/media/5lght4ck/regulator-38.pdf"
      }
    ],
    "reviewedAt": "2026-10-23",
    "lastUpdated": "2026-10-23",
    "adsEligible": false
  },
  {
    "id": "bauchi",
    "name": "Sir Abubakar Tafawa Balewa Airport",
    "iataCode": "BCU",
    "city": "Bauchi",
    "state": "Bauchi State",
    "region": "North",
    "type": "International",
    "coordinates": {
      "lat": 10.4828,
      "lng": 9.744
    },
    "imageUrl": "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=800&q=80",
    "description": "Sir Abubakar Tafawa Balewa Airport serves Bauchi. The state government identifies it as the airport used for its pilgrimage operations.",
    "planningNotes": [
      "Confirm the airport and reporting point with the operating carrier, especially for group or pilgrimage travel. Do not infer a daily scheduled service from an airport’s international designation."
    ],
    "sources": [
      {
        "title": "Bauchi State: pilgrimage return operations",
        "url": "https://home.bauchistate.gov.ng/page/5/?cat=-1"
      },
      {
        "title": "NCAA: Kaduna region airport inventory",
        "url": "https://ncaa.gov.ng/media/pa1mwrhh/regulator-34.pdf"
      }
    ],
    "reviewedAt": "2026-10-23",
    "lastUpdated": "2026-10-23",
    "adsEligible": false
  },
  {
    "id": "gombe",
    "name": "Gombe Lawanti International Airport",
    "iataCode": "GMO",
    "icaoCode": "DNGO",
    "city": "Gombe",
    "state": "Gombe State",
    "region": "North",
    "type": "International",
    "coordinates": {
      "lat": 10.2983,
      "lng": 10.8964
    },
    "imageUrl": "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=800&q=80",
    "description": "Gombe Lawanti Airport serves Gombe State. Gombe is identified in ICAO’s Nigerian aerodrome listing and the NCAA Kaduna region inventory.",
    "planningNotes": [
      "Confirm the flight and the full pickup address with your airline and host. If you are continuing beyond Gombe, arrange the next leg before departure rather than relying on a connection inferred from a map."
    ],
    "sources": [
      {
        "title": "ICAO AMBEX Handbook: Nigerian aerodrome names and codes",
        "url": "https://www.icao.int/sites/default/files/ESAF/APIRG/APIRG-20/Docs/WORKING-PAPERS/Working-Papers-10/WP10e-Appendix-H-APIRG20-AMBEX-HANDBOOK-7thEdition-Amendment-4.pdf"
      },
      {
        "title": "NCAA: Kaduna region airport inventory",
        "url": "https://ncaa.gov.ng/media/pa1mwrhh/regulator-34.pdf"
      }
    ],
    "reviewedAt": "2026-10-23",
    "lastUpdated": "2026-10-23",
    "adsEligible": false
  },
  {
    "id": "makurdi",
    "name": "Makurdi Airport",
    "iataCode": "MDI",
    "icaoCode": "DNMK",
    "city": "Makurdi",
    "state": "Benue State",
    "region": "North",
    "type": "Domestic",
    "coordinates": {
      "lat": 7.7038,
      "lng": 8.6139
    },
    "imageUrl": "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=800&q=80",
    "description": "Makurdi Airport serves Makurdi in Benue State. FAAN places the airport along the Makurdi–Gboko road.",
    "planningNotes": [
      "Check passenger access and an actual operating flight with the airline or arranging organization before traveling to the airport. Inclusion in a directory does not establish that seats are available on a regular scheduled service."
    ],
    "sources": [
      {
        "title": "FAAN airport directory and passenger guidance",
        "url": "https://faan.gov.ng/local-airports/"
      },
      {
        "title": "FAAN Act 2022: airport names and codes (First Schedule)",
        "url": "https://faan.gov.ng/wp-content/uploads/2025/11/FAAN-Act-2022-1.pdf"
      }
    ],
    "reviewedAt": "2026-10-23",
    "lastUpdated": "2026-10-23",
    "adsEligible": false
  },
  {
    "id": "warri",
    "name": "Osubi International Airport (Warri)",
    "iataCode": "QRW",
    "icaoCode": "DNSU",
    "city": "Warri",
    "state": "Delta State",
    "region": "South",
    "type": "Domestic",
    "coordinates": {
      "lat": 5.5967,
      "lng": 5.8176
    },
    "imageUrl": "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=800&q=80",
    "description": "Osubi Airport serves travelers heading to Warri and surrounding parts of Delta State. It is separate from Asaba Airport.",
    "planningNotes": [
      "Use QRW when checking a Warri/Osubi booking. If your journey continues to an industrial site, agree the collection arrangements with the receiving organization and confirm which airport it expects."
    ],
    "sources": [
      {
        "title": "ICAO AMBEX Handbook: Nigerian aerodrome names and codes",
        "url": "https://www.icao.int/sites/default/files/ESAF/APIRG/APIRG-20/Docs/WORKING-PAPERS/Working-Papers-10/WP10e-Appendix-H-APIRG20-AMBEX-HANDBOOK-7thEdition-Amendment-4.pdf"
      },
      {
        "title": "FAAN local service charter: Osubi airport",
        "url": "https://faan.gov.ng/wp-content/uploads/2026/05/Local-Charter-NOV-2025.pdf"
      }
    ],
    "reviewedAt": "2026-10-23",
    "lastUpdated": "2026-10-23",
    "adsEligible": false
  },
  {
    "id": "minna",
    "name": "Minna Airport",
    "iataCode": "MXJ",
    "icaoCode": "DNMN",
    "city": "Minna",
    "state": "Niger State",
    "region": "North",
    "type": "Domestic",
    "coordinates": {
      "lat": 9.6522,
      "lng": 6.4627
    },
    "imageUrl": "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=800&q=80",
    "description": "Minna Airport serves Minna in Niger State. FAAN identifies the airport with the Maikunkele/Bosso area.",
    "planningNotes": [
      "Check the exact destination before purchasing: Minna in Niger State is in Nigeria, not the neighboring Republic of Niger. Confirm scheduled availability with the carrier for the date you need."
    ],
    "sources": [
      {
        "title": "FAAN airport directory and passenger guidance",
        "url": "https://faan.gov.ng/local-airports/"
      },
      {
        "title": "FAAN Act 2022: airport names and codes (First Schedule)",
        "url": "https://faan.gov.ng/wp-content/uploads/2025/11/FAAN-Act-2022-1.pdf"
      }
    ],
    "reviewedAt": "2026-10-23",
    "lastUpdated": "2026-10-23",
    "adsEligible": false
  },
  {
    "id": "katsina",
    "name": "Umaru Musa Yar'Adua Airport",
    "iataCode": "DKA",
    "icaoCode": "DNKT",
    "city": "Katsina",
    "state": "Katsina State",
    "region": "North",
    "type": "International",
    "coordinates": {
      "lat": 13.0078,
      "lng": 7.6605
    },
    "imageUrl": "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=800&q=80",
    "description": "Umaru Musa Yar’Adua Airport serves Katsina. FAAN locates the airport southwest of the city.",
    "planningNotes": [
      "For group or pilgrimage departures, use the reporting instructions issued for that flight. For other journeys, confirm your terminal and check-in deadline directly with the airline."
    ],
    "sources": [
      {
        "title": "FAAN airport directory and passenger guidance",
        "url": "https://faan.gov.ng/local-airports/"
      },
      {
        "title": "FAAN Act 2022: airport names and codes (First Schedule)",
        "url": "https://faan.gov.ng/wp-content/uploads/2025/11/FAAN-Act-2022-1.pdf"
      }
    ],
    "reviewedAt": "2026-10-23",
    "lastUpdated": "2026-10-23",
    "adsEligible": false
  },
  {
    "id": "zaria",
    "name": "Zaria Airport",
    "iataCode": "ZAR",
    "icaoCode": "DNZA",
    "city": "Zaria",
    "state": "Kaduna State",
    "region": "North",
    "type": "Domestic",
    "coordinates": {
      "lat": 11.1302,
      "lng": 7.6858
    },
    "imageUrl": "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=800&q=80",
    "description": "Zaria aerodrome is associated with the Nigerian College of Aviation Technology in Zaria, Kaduna State.",
    "planningNotes": [
      "For a training visit, obtain arrival and access instructions from NCAT. Do not treat the presence of an airport code as evidence of bookable passenger service; check your travel arrangements separately."
    ],
    "sources": [
      {
        "title": "Nigerian College of Aviation Technology, Zaria",
        "url": "https://ncat.gov.ng/"
      },
      {
        "title": "FAAN Act 2022: airport names and codes (First Schedule)",
        "url": "https://faan.gov.ng/wp-content/uploads/2025/11/FAAN-Act-2022-1.pdf"
      }
    ],
    "reviewedAt": "2026-10-23",
    "lastUpdated": "2026-10-23",
    "adsEligible": false
  },
  {
    "id": "yola",
    "name": "Yola Airport",
    "iataCode": "YOL",
    "icaoCode": "DNYO",
    "city": "Yola",
    "state": "Adamawa State",
    "region": "North",
    "type": "International",
    "coordinates": {
      "lat": 9.2575,
      "lng": 12.4304
    },
    "imageUrl": "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=800&q=80",
    "description": "Yola Airport serves Yola and the Jimeta area in Adamawa State. FAAN identifies it as a northeastern Nigerian airport.",
    "planningNotes": [
      "Arrange the onward pickup with your host using YOL and your flight number. For travel beyond the city, check destination-specific conditions and allow for the road leg separately from the flight."
    ],
    "sources": [
      {
        "title": "FAAN airport directory and passenger guidance",
        "url": "https://faan.gov.ng/local-airports/"
      },
      {
        "title": "FAAN Act 2022: airport names and codes (First Schedule)",
        "url": "https://faan.gov.ng/wp-content/uploads/2025/11/FAAN-Act-2022-1.pdf"
      }
    ],
    "reviewedAt": "2026-10-23",
    "lastUpdated": "2026-10-23",
    "adsEligible": false
  },
  {
    "id": "owerri",
    "name": "Sam Mbakwe International Cargo Airport",
    "iataCode": "QOW",
    "icaoCode": "DNIM",
    "city": "Owerri",
    "state": "Imo State",
    "region": "East",
    "type": "International",
    "coordinates": {
      "lat": 5.4271,
      "lng": 7.206
    },
    "imageUrl": "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=800&q=80",
    "description": "Sam Mbakwe International Cargo Airport serves Owerri and surrounding parts of Imo State. FAAN publishes directions to its passenger assistance desk.",
    "planningNotes": [
      "For terminal assistance, the FAAN guide identifies desks in the departure and baggage-claim areas. Report a checked-baggage problem to your airline before leaving; terminal lost property is a separate inquiry."
    ],
    "sources": [
      {
        "title": "FAAN passenger assistance and terminal directions",
        "url": "https://faan.gov.ng/passenger-experience/"
      },
      {
        "title": "ICAO AMBEX Handbook: Nigerian aerodrome names and codes",
        "url": "https://www.icao.int/sites/default/files/ESAF/APIRG/APIRG-20/Docs/WORKING-PAPERS/Working-Papers-10/WP10e-Appendix-H-APIRG20-AMBEX-HANDBOOK-7thEdition-Amendment-4.pdf"
      },
      {
        "title": "FAAN Act 2022: airport names and codes (First Schedule)",
        "url": "https://faan.gov.ng/wp-content/uploads/2025/11/FAAN-Act-2022-1.pdf"
      }
    ],
    "reviewedAt": "2026-10-23",
    "lastUpdated": "2026-10-23",
    "adsEligible": false
  },
  {
    "id": "uyo",
    "name": "Victor Attah International Airport",
    "iataCode": "QUO",
    "icaoCode": "DNAI",
    "city": "Uyo",
    "state": "Akwa Ibom State",
    "region": "South",
    "type": "International",
    "coordinates": {
      "lat": 4.8725,
      "lng": 8.093
    },
    "imageUrl": "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=800&q=80",
    "description": "Victor Attah International Airport serves Uyo in Akwa Ibom State. It is also widely referred to as Uyo or Ibom Airport.",
    "planningNotes": [
      "Ibom Air announced that its Uyo operations moved to the new terminal from 14 June 2026. Follow the latest terminal instructions on your booking and reconfirm with your airline; this notice does not establish terminal arrangements for other carriers."
    ],
    "sources": [
      {
        "title": "Ibom Air: Uyo terminal move, June 2026",
        "url": "https://www.ibomair.com/ibom-air-moves-uyo-flights-to-the-new-ultramodern-terminal/"
      }
    ],
    "reviewedAt": "2026-10-23",
    "lastUpdated": "2026-10-23",
    "adsEligible": false
  },
  {
    "id": "sokoto",
    "name": "Sultan Sadiq Abubakar III Airport",
    "iataCode": "SKO",
    "icaoCode": "DNSO",
    "city": "Sokoto",
    "state": "Sokoto State",
    "region": "North",
    "type": "International",
    "coordinates": {
      "lat": 12.9163,
      "lng": 5.2071
    },
    "imageUrl": "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=800&q=80",
    "description": "Sultan Abubakar III Airport serves Sokoto. FAAN identifies the airport southwest of the city and describes its use for pilgrimage operations.",
    "planningNotes": [
      "Group departures may have a dedicated reporting arrangement. Confirm that arrangement with the carrier or organizer and avoid assuming that a general check-in time applies to a pilgrimage flight."
    ],
    "sources": [
      {
        "title": "FAAN airport directory and passenger guidance",
        "url": "https://faan.gov.ng/local-airports/"
      },
      {
        "title": "FAAN Act 2022: airport names and codes (First Schedule)",
        "url": "https://faan.gov.ng/wp-content/uploads/2025/11/FAAN-Act-2022-1.pdf"
      }
    ],
    "reviewedAt": "2026-10-23",
    "lastUpdated": "2026-10-23",
    "adsEligible": false
  }
];
export const getAirportById = (id: string): Airport | undefined => airports.find(airport => airport.id === id);
export const getAirportByIATA = (code: string): Airport | undefined => airports.find(airport => airport.iataCode === code.toUpperCase());
export const getAirportsByRegion = (region: string): Airport[] => airports.filter(airport => airport.region === region);
export const getAirportsByType = (type: 'International' | 'Domestic'): Airport[] => airports.filter(airport => airport.type === type);
export const searchAirports = (query: string): Airport[] => {
  const term = query.trim().toLowerCase();
  return airports.filter(airport => [airport.name, airport.city, airport.iataCode, airport.state].some(value => value.toLowerCase().includes(term)));
};
export const getAirportCoordinates = () => airports.map(({ id, name, iataCode, coordinates }) => ({ id, name, iataCode, ...coordinates }));
